import HBIcon from "@/components/HBIcon";
import classNames from "classnames";
import { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./index.module.less";

interface FontDetailPreviewProps {
  pin: IPin;
  active?: boolean;
  onBackgroundClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
}

// 基础字号
const BASE_TITLE_SIZE = 64;
const BASE_BODY_SIZE = 32;

export default function FontDetailPreview({
  pin,
  active = true,
  onBackgroundClick,
}: FontDetailPreviewProps) {
  const [scaleRatio, setScaleRatio] = useState(0); // 缩放比例，0 表示 100%
  const [shortFontFamily, setShortFontFamily] = useState("");
  const [longFontFamily, setLongFontFamily] = useState("");

  const fontInfo = useMemo(() => {
    return pin?.file_material?.font_info || pin?.material_info?.font_info;
  }, [pin]);

  // 判断字体数据是否完整
  const hasCompleteFontData = useMemo(() => {
    if (!fontInfo) {
      return false;
    }

    // 检查数据完整性：至少有一个 demo_text 和对应的字体文件
    const hasShortDemo = !!(
      fontInfo.short_demo_text && fontInfo.short_subset_file
    );
    const hasLongDemo = !!(
      fontInfo.long_demo_text && fontInfo.long_subset_file
    );

    // 只有当至少有一个完整的组合（demo_text + 字体文件）时，数据才算完整
    return hasShortDemo || hasLongDemo;
  }, [fontInfo]);

  // 计算当前字号
  const titleFontSize = useMemo(() => {
    return BASE_TITLE_SIZE * (1 + scaleRatio);
  }, [scaleRatio]);

  const bodyFontSize = useMemo(() => {
    return BASE_BODY_SIZE * (1 + scaleRatio);
  }, [scaleRatio]);

  // 计算百分比显示
  const scalePercentage = useMemo(() => {
    const percentage = Math.round((1 + scaleRatio) * 100);
    return `${percentage}%`;
  }, [scaleRatio]);

  // 处理长文本换行
  const longDemoTextLines = useMemo(() => {
    if (!fontInfo?.long_demo_text) return [];
    return fontInfo.long_demo_text.split("\n").filter((line) => line.trim());
  }, [fontInfo]);

  // 容器样式（CSS 变量）
  const containerStyle = useMemo(() => {
    return {
      "--title-font-family": shortFontFamily,
      "--title-font-size": `${titleFontSize}px`,
      "--body-font-family": longFontFamily,
      "--body-font-size": `${bodyFontSize}px`,
    } as React.CSSProperties;
  }, [shortFontFamily, titleFontSize, longFontFamily, bodyFontSize]);

  // 加载字体
  const loadFont = useCallback(
    async (fontUrl: string, fontFamilyName: string) => {
      if (!fontUrl) return null;

      try {
        const fontFace = new FontFace(fontFamilyName, `url(${fontUrl})`);
        await fontFace.load();
        document.fonts.add(fontFace);
        return fontFamilyName;
      } catch (error) {
        console.error("Failed to load font:", fontUrl, error);
        return null;
      }
    },
    []
  );

  // 初始化字体
  const initFonts = useCallback(async () => {
    if (!fontInfo) return;

    const shortFontUrl = fontInfo.short_subset_file;
    const longFontUrl = fontInfo.long_subset_file;
    const materialId = pin?.file_material?.material_id || pin?.pin_id;

    if (shortFontUrl && materialId) {
      const fontName = `font-short-${materialId}`;
      const loadedFont = await loadFont(shortFontUrl, fontName);
      if (loadedFont) {
        setShortFontFamily(loadedFont);
      }
    }

    if (longFontUrl && materialId) {
      const fontName = `font-long-${materialId}`;
      const loadedFont = await loadFont(longFontUrl, fontName);
      if (loadedFont) {
        setLongFontFamily(loadedFont);
      }
    }
  }, [fontInfo, pin, loadFont]);

  // 放大
  const handleZoomIn = useCallback(() => {
    setScaleRatio((prev) => Math.min(prev + 0.1, 2)); // 最大 300%
  }, []);

  // 缩小
  const handleZoomOut = useCallback(() => {
    setScaleRatio((prev) => Math.max(prev - 0.1, -0.9)); // 最小 10%
  }, []);

  // 恢复
  const handleReset = useCallback(() => {
    setScaleRatio(0);
  }, []);

  // 监听 pin 变化，重新加载字体
  useEffect(() => {
    const materialId = pin?.file_material?.material_id || pin?.pin_id;
    if (materialId && fontInfo) {
      initFonts();
    }
  }, [pin?.file_material?.material_id, pin?.pin_id, fontInfo, initFonts]);

  return (
    <div
      className={styles.container}
      style={containerStyle}
      onClick={(e) => {
        if (e.target === e.currentTarget && onBackgroundClick) {
          onBackgroundClick(e);
        }
      }}
    >
      {/* 数据不全时显示预览图 */}
      {!hasCompleteFontData && (
        <div className={styles.previewImageContainer}>
          {fontInfo?.alias_preview_url && (
            <img
              src={fontInfo.alias_preview_url}
              alt={fontInfo.alias || pin?.raw_text || ""}
              className={styles.previewImage}
            />
          )}
        </div>
      )}

      {/* 数据完整时显示字体预览 */}
      {hasCompleteFontData && (
        <>
          {/* 字体预览区域 */}
          <div className={styles.previewArea}>
            {/* 标题文字 */}
            {fontInfo?.short_demo_text && (
              <div className={styles.titleText}>{fontInfo.short_demo_text}</div>
            )}

            {/* 正文文字 */}
            {fontInfo?.long_demo_text && (
              <div className={styles.bodyText}>
                {longDemoTextLines.map((line, index) => (
                  <p
                    key={index}
                    className={classNames({
                      [styles.lastParagraph]:
                        index === longDemoTextLines.length - 1,
                    })}
                  >
                    {line}
                  </p>
                ))}
              </div>
            )}
          </div>

          {/* 控制区域 */}
          <div className={styles.controlArea}>
            <div className={styles.controlGroup}>
              {/* 缩小按钮 */}
              <button
                className={styles.controlButton}
                onClick={handleZoomOut}
                disabled={scaleRatio <= -0.9}
                title="缩小"
              >
                <HBIcon type="minus" />
              </button>

              {/* 百分比显示（恢复按钮） */}
              <button
                className={styles.scaleButton}
                onClick={handleReset}
                disabled={scaleRatio === 0}
                title="恢复"
              >
                {scalePercentage}
              </button>

              {/* 放大按钮 */}
              <button
                className={styles.controlButton}
                onClick={handleZoomIn}
                disabled={scaleRatio >= 2}
                title="放大"
              >
                <HBIcon type="plus" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
